<template>
  <vx-card title="Solar Production" class="mb-base">
    <div slot="no-body" class="p-4">
      <!-- Header: station selector + last update -->
      <div class="vx-row mb-4 items-center">
        <div class="vx-col w-full md:w-1/2 mb-2 md:mb-0">
          <label class="text-sm font-semibold mr-2">Plant</label>
          <v-select
            :options="stationOptions"
            v-model="selectedStationOption"
            :clearable="false"
            :reduce="o => o.value"
            placeholder="Select a plant..."
            :disabled="loadingStations || stationOptions.length === 0"
          />
        </div>
        <div class="vx-col w-full md:w-1/2 text-right text-sm" v-if="realtime && realtime.lastUpdateTime">
          <feather-icon icon="ClockIcon" svgClasses="h-4 w-4" class="align-middle mr-1" />
          <span>Updated {{ formatRelative(realtime.lastUpdateTime) }}</span>
        </div>
      </div>

      <div v-if="errorMessage" class="text-danger text-sm mb-4">
        {{ errorMessage }}
      </div>

      <!-- Live banner -->
      <div class="vx-row mb-6" v-if="hasStation">
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-tile">
            <feather-icon icon="ZapIcon" svgClasses="h-6 w-6 text-warning" />
            <div class="solar-tile-value">{{ formatPower(realtime.generationPower) }}</div>
            <div class="solar-tile-label">Live production</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-tile">
            <feather-icon icon="HomeIcon" svgClasses="h-6 w-6 text-primary" />
            <div class="solar-tile-value">{{ formatPower(realtime.usePower) }}</div>
            <div class="solar-tile-label">Consumption</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-tile">
            <feather-icon icon="BatteryChargingIcon" svgClasses="h-6 w-6 text-success" />
            <div class="solar-tile-value">{{ realtime.batterySoc != null ? Math.round(realtime.batterySoc) + ' %' : '–' }}</div>
            <div class="solar-tile-label">Battery SOC</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-tile">
            <feather-icon icon="SunIcon" svgClasses="h-6 w-6" :svgStyle="{ color: '#f0a500' }" />
            <div class="solar-tile-value">{{ currentWeatherText }}</div>
            <div class="solar-tile-label">Current weather</div>
          </div>
        </div>
      </div>

      <!-- Weekly KPI tiles -->
      <div class="vx-row mb-6" v-if="hasStation && weeklyTotals">
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-kpi solar-kpi-prod">
            <div class="solar-kpi-value">{{ weeklyTotals.generation.toFixed(1) }} kWh</div>
            <div class="solar-kpi-label">Generated (7d)</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-kpi solar-kpi-self">
            <div class="solar-kpi-value">{{ weeklyTotals.selfConsumed.toFixed(1) }} kWh</div>
            <div class="solar-kpi-label">Self-consumed (7d)</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-kpi solar-kpi-rate">
            <div class="solar-kpi-value">{{ weeklyTotals.selfRate.toFixed(0) }} %</div>
            <div class="solar-kpi-label">Self-consumption rate</div>
          </div>
        </div>
        <div class="vx-col w-1/2 sm:w-1/4 mb-2">
          <div class="solar-kpi solar-kpi-grid">
            <div class="solar-kpi-value">{{ weeklyTotals.injected.toFixed(1) }} kWh</div>
            <div class="solar-kpi-label">Grid export</div>
          </div>
        </div>
      </div>

      <!-- Charts row -->
      <div class="vx-row" v-if="hasStation">
        <div class="vx-col w-full lg:w-1/2 mb-4">
          <h6 class="mb-2">Production over the last 7 days</h6>
          <vue-apex-charts
            v-if="historyChart.series[0].data.length > 0"
            type="bar"
            height="280"
            :options="historyChart.options"
            :series="historyChart.series"
          />
          <div v-else class="text-sm text-grey">No data yet.</div>
        </div>
        <div class="vx-col w-full lg:w-1/2 mb-4">
          <h6 class="mb-2">Weather forecast &amp; estimated production (next 7 days)</h6>
          <vue-apex-charts
            v-if="forecastChart.series[0].data.length > 0"
            type="bar"
            height="280"
            :options="forecastChart.options"
            :series="forecastChart.series"
          />
          <div v-else class="text-sm text-grey">No forecast available.</div>
        </div>
      </div>

      <div v-if="!hasStation && !loadingStations" class="text-sm text-grey">
        No plant available.
      </div>
    </div>
  </vx-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import vSelect from "vue-select";

export default {
  name: "SolarProductionModule",
  components: { VueApexCharts, vSelect },
  data() {
    return {
      selectedStationId: null,
      loadingStations: false,
      errorMessage: null,
      realtimeTimer: null,
    };
  },
  computed: {
    stations() {
      return this.$store.state.dataListDashboard.solarStations || [];
    },
    realtime() {
      return this.$store.state.dataListDashboard.solarRealtime || {};
    },
    history() {
      return this.$store.state.dataListDashboard.solarHistory || [];
    },
    forecast() {
      return this.$store.state.dataListDashboard.solarForecast;
    },
    historyDay() {
      return this.$store.state.dataListDashboard.solarHistoryDay || [];
    },
    isAdmin() {
      try {
        const r = (localStorage.getItem("login-roles") || "").toLowerCase();
        return r.includes("admin");
      } catch (e) {
        return false;
      }
    },
    solarScores() {
      return this.$store.state.dataListDashboard.solarScores || [];
    },
    solarAlerts() {
      return (this.$store.state.dataListDashboard.solarCriticalAlerts || {}).items || [];
    },
    solarCriticalCount() {
      return (this.$store.state.dataListDashboard.solarCriticalAlerts || {}).criticalCount || 0;
    },
    solarWarningCount() {
      return (this.$store.state.dataListDashboard.solarCriticalAlerts || {}).warningCount || 0;
    },
    hasStation() {
      return !!this.selectedStationId;
    },
    selectedStation() {
      return this.stations.find((s) => String(s.id) === String(this.selectedStationId));
    },
    stationOptions() {
      return this.stations.map((s) => ({
        label: s.name + (s.installedCapacity ? ` — ${s.installedCapacity} kWp` : ""),
        value: s.id,
      }));
    },
    selectedStationOption: {
      get() {
        return this.stationOptions.find((o) => String(o.value) === String(this.selectedStationId)) || null;
      },
      set(val) {
        this.selectedStationId = val && val.value !== undefined ? val.value : val;
      },
    },
    currentWeatherText() {
      const c = this.forecast && this.forecast.current;
      if (!c) return "–";
      const t = c.temperature_2m != null ? `${Math.round(c.temperature_2m)}°C` : "";
      const cc = c.cloud_cover != null ? `${Math.round(c.cloud_cover)}% clouds` : "";
      return [t, cc].filter(Boolean).join(" · ") || "–";
    },
    weeklyTotals() {
      if (!this.history.length) return null;
      const sum = (k) => this.history.reduce((acc, d) => acc + (Number(d[k]) || 0), 0);
      const generation = sum("generationValue");
      const use = sum("useValue");
      const buy = sum("buyValue");
      const inject = sum("gridValue");
      const selfConsumed = Math.max(0, generation - inject);
      const selfRate = use > 0 ? Math.max(0, Math.min(100, ((use - buy) / use) * 100)) : 0;
      return { generation, selfConsumed, selfRate, injected: inject };
    },
    historyChart() {
      const labels = this.history.map((d) => this.shortDate(d.date));
      const values = this.history.map((d) => Number(d.generationValue) || 0);
      return {
        series: [{ name: "Generated (kWh)", data: values }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: "inherit" },
          colors: ["#28C76F"],
          plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
          dataLabels: { enabled: false },
          xaxis: { categories: labels },
          yaxis: { labels: { formatter: (v) => v.toFixed(0) + " kWh" } },
          tooltip: { y: { formatter: (v) => v.toFixed(2) + " kWh" } },
          grid: { borderColor: "#eee" },
        },
      };
    },
    dayChart() {
      const items = this.historyDay;
      const labels = items.map((d) => this.shortTime(d.dateTime));
      const pv   = items.map((d) => Number(d.generationPower) || 0);
      const cons = items.map((d) => Number(d.usePower) || 0);
      const consAllZero = cons.every((v) => v === 0);
      const series = consAllZero
        ? [{ name: "PV (kW)", data: pv }]
        : [
            { name: "PV (kW)", data: pv },
            { name: "Consumption (kW)", data: cons },
          ];
      const colors = consAllZero ? ["#5A88F0"] : ["#5A88F0", "#EA5455"];
      return {
        series,
        options: {
          chart: {
            toolbar: { show: false },
            fontFamily: "inherit",
            zoom: { enabled: false },
          },
          colors,
          dataLabels: { enabled: false },
          stroke: { curve: "smooth", width: 2 },
          fill: {
            type: "gradient",
            gradient: { opacityFrom: 0.45, opacityTo: 0.05, shadeIntensity: 1, stops: [0, 90, 100] },
          },
          xaxis: {
            categories: labels,
            tickAmount: 8,
            labels: { style: { fontSize: "10px" } },
          },
          yaxis: { labels: { formatter: (v) => Number(v).toFixed(1) + " kW" } },
          tooltip: { y: { formatter: (v) => Number(v).toFixed(2) + " kW" } },
          legend: { position: "top", horizontalAlign: "center" },
          grid: { borderColor: "#eee" },
        },
      };
    },
    forecastChart() {
      const f = this.forecast && this.forecast.daily;
      const station = this.selectedStation;
      if (!f || !f.time || !station) {
        return { series: [{ name: "Estimated kWh", data: [] }], options: {} };
      }
      const cap = Number(station.installedCapacity) || 0;
      const factor = 0.15;
      const labels = f.time.map((t) => this.shortDate(t));
      const radiation = f.shortwave_radiation_sum || [];
      const estimated = radiation.map((mj) => (Number(mj) || 0) * cap * factor);
      return {
        series: [{ name: "Estimated kWh", data: estimated.map((v) => Number(v.toFixed(1))) }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: "inherit" },
          colors: ["#FF9F43"],
          plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
          dataLabels: { enabled: false },
          xaxis: { categories: labels },
          yaxis: { labels: { formatter: (v) => v.toFixed(0) + " kWh" } },
          tooltip: { y: { formatter: (v) => v.toFixed(1) + " kWh" } },
          grid: { borderColor: "#eee" },
        },
      };
    },
  },
  watch: {
    selectedStationId(val, old) {
      if (val && val !== old) this.loadStationData();
    },
  },
  mounted() {
    this.loadStations();
    // Defensive fetch: navbar polls these every 30s, but if the popup is opened
    // before the first navbar tick we want fresh score + alerts immediately.
    this.$store.dispatch("dataListDashboard/fetchSolarScores").catch(() => {});
    this.$store.dispatch("dataListDashboard/fetchSolarCriticalAlerts").catch(() => {});
    this.realtimeTimer = setInterval(() => {
      if (this.selectedStationId) {
        this.$store
          .dispatch("dataListDashboard/fetchSolarRealtime", this.selectedStationId)
          .catch(() => {});
      }
    }, 5 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.realtimeTimer) clearInterval(this.realtimeTimer);
  },
  methods: {
    async loadStations() {
      this.loadingStations = true;
      this.errorMessage = null;
      try {
        await this.$store.dispatch("dataListDashboard/fetchSolarStations");
        if (this.stations.length > 0 && !this.selectedStationId) {
          this.selectedStationId = this.stations[0].id;
        }
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.message) ||
          "Unable to load the list of plants.";
      } finally {
        this.loadingStations = false;
      }
    },
    loadStationData() {
      const id = this.selectedStationId;
      if (!id) return;
      this.$store.dispatch("dataListDashboard/fetchSolarRealtime", id).catch(() => {});
      this.$store.dispatch("dataListDashboard/fetchSolarHistory", id).catch(() => {});
      this.$store.dispatch("dataListDashboard/fetchSolarHistoryDay", id).catch(() => {});
      this.$store.dispatch("dataListDashboard/fetchSolarForecast", id).catch(() => {});
    },
    formatPower(watts) {
      if (watts === null || watts === undefined || isNaN(watts)) return "–";
      const w = Number(watts);
      if (Math.abs(w) >= 1000) return (w / 1000).toFixed(2) + " kW";
      return Math.round(w) + " W";
    },
    formatRelative(when) {
      if (!when) return "";
      // Accept either Unix-seconds (existing realtime.lastUpdateTime contract)
      // or ISO/parsable date string (alerts.openedAt — TIMESTAMPTZ from PG).
      let ms;
      if (typeof when === "number" || /^\d+$/.test(String(when))) {
        ms = Number(when) * 1000;
      } else {
        ms = Date.parse(when);
      }
      if (!ms || isNaN(ms)) return String(when);
      const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
      if (diffSec < 60) return "just now";
      const min = Math.floor(diffSec / 60);
      if (min < 60) return `${min} min ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr} h ago`;
      const days = Math.floor(hr / 24);
      return `${days} d ago`;
    },
    shortDate(d) {
      if (!d) return "";
      const date = typeof d === "string" ? new Date(d) : d;
      if (isNaN(date.getTime())) return String(d);
      return date.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit" });
    },
    shortTime(d) {
      if (!d) return "";
      // Solarman returns "yyyy-MM-dd HH:mm:ss" or unix seconds.
      let date;
      if (typeof d === "number" || /^\d+$/.test(String(d))) {
        date = new Date(Number(d) * 1000);
      } else {
        date = new Date(String(d).replace(" ", "T"));
      }
      if (isNaN(date.getTime())) return String(d);
      return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    },
    scoreClass(s) {
      if (s == null) return "";
      if (s >= 85) return "score-green";
      if (s >= 60) return "score-orange";
      return "score-red";
    },
    levelLabel(lvl) {
      if (lvl === "healthy") return "Healthy";
      if (lvl === "degraded") return "Degraded";
      if (lvl === "critical") return "Critical";
      return lvl || "–";
    },
    ruleLabel(code) {
      if (code === "R1A_ZERO_PROD") return "No production with sun";
      if (code === "R1B_OFFLINE") return "Inverter offline";
      if (code === "R1C_UNDERPROD") return "Under-production";
      if (code && code.startsWith("NATIVE_")) return "Solarman: " + code.replace("NATIVE_", "");
      return code || "–";
    },
  },
};
</script>

<style scoped>
.solar-tile {
  text-align: center;
  padding: 12px 8px;
  background: #f8f9fb;
  border-radius: 8px;
}
.solar-tile-value {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 4px;
}
.solar-tile-label {
  font-size: 0.75rem;
  color: #777;
  margin-top: 2px;
}
.solar-kpi {
  text-align: center;
  padding: 14px 8px;
  border-radius: 8px;
  color: #fff;
}
.solar-kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
}
.solar-kpi-label {
  font-size: 0.75rem;
  opacity: 0.95;
  margin-top: 2px;
}
.solar-kpi-prod  { background: #28C76F; }
.solar-kpi-self  { background: #1E9FF2; }
.solar-kpi-rate  { background: #7367F0; }
.solar-kpi-grid  { background: #FF9F43; }

.solar-score-pill-big {
  display: inline-block;
  min-width: 56px;
  padding: 6px 14px;
  border-radius: 14px;
  font-size: 1.4rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 4px;
}
.solar-score-pill-big.score-green  { background: #28C76F; }
.solar-score-pill-big.score-orange { background: #FF9F43; }
.solar-score-pill-big.score-red    { background: #EA5455; }

.solar-alerts-table th {
  font-weight: 600;
  color: #626262;
  font-size: 11px;
  text-transform: uppercase;
}
.alert-sev-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
}
.alert-sev-pill.sev-critical { background: #EA5455; }
.alert-sev-pill.sev-warning  { background: #FF9F43; }
.alert-sev-pill.sev-info     { background: #1E9FF2; }
</style>
